import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

export const Chatbot = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const sessionId = useRef("chat-" + Math.random().toString(36).slice(2));
  const endRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "bot", text: t("chat_welcome") }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sessionId.current, message: text, lang });
      setMessages((m) => [...m, { role: "bot", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "⚠️ " + (lang === "fr" ? "Assistant indisponible, réessayez." : "Assistant unavailable, try again.") }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="chatbot-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-mango-600 hover:bg-mango-700 text-white shadow-xl grid place-items-center transition-transform hover:scale-105">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[520px] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden fade-up" data-testid="chatbot-panel">
          <div className="bg-emerald-800 text-white px-4 py-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-mango-400" />
            <div>
              <p className="font-display font-bold leading-none">{t("chat_title")}</p>
              <p className="text-[11px] text-emerald-200">AGOO MARKET</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-sand-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-emerald-800 text-white rounded-br-sm" : "bg-white border border-stone-200 text-stone-800 rounded-bl-sm"}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-white border border-stone-200 px-3 py-2 rounded-2xl"><Loader2 className="w-4 h-4 animate-spin text-emerald-800" /></div></div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="p-3 border-t border-stone-200 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("chat_ph")}
              data-testid="chatbot-input"
              className="flex-1 rounded-full border border-stone-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/20" />
            <button type="submit" disabled={loading} data-testid="chatbot-send" className="w-10 h-10 rounded-full bg-mango-600 text-white grid place-items-center shrink-0 hover:bg-mango-700 transition-colors">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
